import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, notification } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import KcManualModal from 'pages/star-kc/components/kc-manual-modal';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { BATCH_MAX_FILES } from '../batch-utils';
import BatchImport from '../components/batch-import';
import ImportFlow from '../components/import-flow';
import MonthCalendar from '../components/month-calendar';
import MonthStatusAlert from '../components/month-status-alert';
import { ImportResult } from '../types';
import { useCalendar } from '../use-calendar';
import { MAX_FILE_MB, MONTH_RE, formatMonth, utcMonth } from '../utils';
import styles from '../style.module.scss';

/**
 * Tab "Data ngày": mỗi ngày nhập file của đúng 1 ngày TikTok; dải lịch cho biết ngày nào
 * đã nhập, ngày nào còn thiếu. "Sửa tay 1 dòng" mở form nhập tay KC (bắt buộc ghi lý do).
 */
const DailyTab = () => {
  // `?month=YYYY-MM`: nút "Mở Data ngày" ở tab khác mở đúng tháng cần bù ngày.
  const [params] = useSearchParams();
  const urlMonth = params.get('month');
  const linkedMonth = urlMonth && MONTH_RE.test(urlMonth) ? urlMonth : null;
  const [month, setMonth] = useState(() => linkedMonth || utcMonth());
  // Người dùng đã tự chọn tháng thì không tự nhảy theo "today" của máy chủ nữa.
  const monthPicked = useRef(!!linkedMonth);

  useEffect(() => {
    if (!linkedMonth) return;
    monthPicked.current = true;
    setMonth(linkedMonth);
  }, [linkedMonth]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  // Chọn từ 2 file trở lên: nhập hàng loạt (mỗi file tự nhận ngày theo cột "Giai đoạn dữ liệu").
  const [batchFiles, setBatchFiles] = useState<File[] | null>(null);
  const [batchId, setBatchId] = useState(0);
  const { data: calendar, loading } = useCalendar(month, refreshKey);
  // Nút "Sửa tay 1 dòng" chỉ hiện khi GET /approvals/me báo KC_MANUAL là true (Admin bật / tắt
  // ô này ở trang Quyền hạn). Đang hỏi hoặc hỏi lỗi → ẩn nút (đặc tả đợt 2, mục 5).
  const [canManualKc, setCanManualKc] = useState(false);

  useEffect(() => {
    let cancelled = false;
    AxiosUtils.get(API.approvalsMe)
      .then((res: any) => {
        if (!cancelled) setCanManualKc(res?.data?.can?.KC_MANUAL === true);
      })
      .catch(() => {
        if (!cancelled) setCanManualKc(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Tháng mặc định là tháng hiện tại theo giờ TikTok (UTC+0) do máy chủ báo; đồng hồ máy
  // lệch quanh lúc sang tháng thì theo máy chủ.
  const serverMonth = calendar?.today?.slice(0, 7);
  useEffect(() => {
    if (monthPicked.current || !serverMonth || serverMonth === month) return;
    monthPicked.current = true;
    setMonth(serverMonth);
  }, [serverMonth, month]);

  const refresh = () => setRefreshKey((key) => key + 1);

  const onApplied = (result: ImportResult) => {
    // Vừa nhập ngày của tháng khác tháng đang xem → chuyển dải lịch sang tháng đó.
    if (result.month && result.month !== month) {
      monthPicked.current = true;
      setMonth(result.month);
      return;
    }
    refresh();
  };

  const currentMonth = serverMonth || utcMonth();

  const pickMany = (files: File[]) => {
    const okType = files.filter((f) => /\.(xlsx|csv)$/i.test(f.name));
    const okSize = okType.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024);
    const skipped = files.length - okSize.length;
    if (skipped) {
      notification.warning({
        message: `Bỏ qua ${skipped} file`,
        description: `Chỉ nhận file .xlsx / .csv tối đa ${MAX_FILE_MB} MB.`,
      });
    }
    if (okSize.length > BATCH_MAX_FILES) {
      notification.error({
        message: 'Quá nhiều file',
        description: `Mỗi lượt tối đa ${BATCH_MAX_FILES} file (một tháng). Hãy chia thành nhiều lượt.`,
      });
      return;
    }
    if (!okSize.length) return;
    setBatchId((id) => id + 1);
    setBatchFiles(okSize);
  };

  const onBatchApplied = (lastMonth: string | null) => {
    if (lastMonth && lastMonth !== month) {
      monthPicked.current = true;
      setMonth(lastMonth);
      return;
    }
    refresh();
  };

  return (
    <div>
      <Card className={styles.section}>
        <div className={styles.head}>
          <h2 className={styles.headTitle}>
            {batchFiles ? 'Nhập nhiều ngày một lượt' : 'Nhập file của 1 ngày'}
          </h2>
          {canManualKc && (
            <div className={styles.headActions}>
              <Button icon={<EditOutlined />} onClick={() => setManualOpen(true)}>
                Sửa tay 1 dòng
              </Button>
            </div>
          )}
        </div>
        {batchFiles ? (
          <BatchImport
            key={batchId}
            files={batchFiles}
            onApplied={onBatchApplied}
            onClose={() => setBatchFiles(null)}
          />
        ) : (
          <ImportFlow
            kind="DAILY"
            onApplied={onApplied}
            onPickMany={pickMany}
            hint={
              <>
                File "Dữ liệu nhà sáng tạo" tải từ TikTok LIVE Backstage, chọn đúng 1 ngày rồi xuất.
                Chọn nhiều file một lúc (tối đa {BATCH_MAX_FILES}) để nhập nhiều ngày: hệ thống tự
                đọc cột "Giai đoạn dữ liệu" để biết file là ngày nào. File cộng dồn nhiều ngày sẽ bị
                chặn; file cả tháng nhập ở tab Data tháng. Ghép theo UID (cột B), có xem trước; nhập
                lại cùng ngày thì ghi đè, không cộng hai lần.
              </>
            }
          />
        )}
      </Card>

      <Card className={styles.section}>
        <div className={styles.head}>
          <h2 className={styles.headTitle}>Lịch nhập tháng {formatMonth(month)}</h2>
          <div className={styles.headActions}>
            <DatePicker
              picker="month"
              allowClear={false}
              format="MM/YYYY"
              value={dayjs(`${month}-01`)}
              disabledDate={(date) => date.format('YYYY-MM') > currentMonth}
              onChange={(date) => {
                if (!date) return;
                monthPicked.current = true;
                setMonth(date.format('YYYY-MM'));
              }}
            />
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              Tải lại
            </Button>
          </div>
        </div>
        <MonthStatusAlert status={calendar?.status} view="ngay" />
        <MonthCalendar data={calendar} loading={loading} />
      </Card>

      {canManualKc && (
        <KcManualModal open={manualOpen} onClose={() => setManualOpen(false)} onSuccess={refresh} />
      )}
    </div>
  );
};

export default DailyTab;
